import type { ReactNode } from 'react'
import { AlertTriangle, Check } from 'lucide-react'

import { cn } from '@/lib/utils'

import { categoryTone, formatJobRef, initialOf, PROBLEM_RED } from './schedule-styles'

/** Round initial for worker rows and panel items; dimmed when the worker can't take the selected job. */
export function WorkerAvatar({ name, dim = false, size = 'md' }: { name: string; dim?: boolean; size?: 'md' | 'lg' }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'inline-flex flex-none items-center justify-center rounded-full font-bold',
        size === 'lg' ? 'h-11 w-11 text-lg' : 'h-[34px] w-[34px] text-sm',
        dim ? 'bg-[#E7EBE6] text-[#3C4B43]' : 'bg-[#DDF1E3] text-[#14532D]',
      )}
    >
      {initialOf(name)}
    </span>
  )
}

export function CategoryPill({ name }: { name: string | null }) {
  const tone = categoryTone(name)
  return (
    <span className="rounded-full px-2.5 py-0.5 text-xs font-semibold" style={{ background: tone.bg, color: tone.fg }}>
      {name || 'No category'}
    </span>
  )
}

export function CompletedIcon({ className }: { className?: string }) {
  return <Check className={cn('h-3 w-3 flex-none', className)} strokeWidth={3} aria-hidden="true" />
}

/** Job reference line under a job's title / category row. */
export function JobRef({ jobRef }: { jobRef: string | null }) {
  const text = formatJobRef(jobRef)
  return text ? <p className="text-[13px] font-semibold tabular-nums text-[#4A5A51]">{text}</p> : null
}

export function WarningIcon({ className }: { className?: string }) {
  return <AlertTriangle className={cn('h-3 w-3 flex-none', className)} style={{ color: PROBLEM_RED }} strokeWidth={2.6} aria-hidden="true" />
}

/** Section heading inside the side panel. */
export function PanelHeading({ children, tone = 'neutral' }: { children: ReactNode; tone?: 'neutral' | 'green' | 'red' }) {
  return (
    <h3
      className={cn(
        'px-[18px] pb-1 pt-3.5 text-[13px] font-bold uppercase tracking-[0.4px]',
        tone === 'green' && 'text-[#14532D]',
        tone === 'red' && 'text-[#8A1C12]',
        tone === 'neutral' && 'text-[#3C4B43]',
      )}
    >
      {children}
    </h3>
  )
}
